import React from 'react'
import { useTheme } from '@emotion/react'
import { useTranslation } from 'react-i18next'
import {
    calculateIssueTotalOfferingSize,
    calculateSumSubscriptionCommitmentInPercentage,
    getLevelName,
    getSummarizedFeeLevels,
    randomBlue,
} from '../utils'
import { valueFormatter } from './utils'
import { CommitChart } from './CommitChart'
import { EnrichedIssueEntity } from '../../../types/IssueEntity'
import { roundFloat } from '../../../packages/utils/numberFormatters'

type SerieType = {
    stack: string
    label: string
    data: number[]
    valueFormatter: (value: number) => string
    color: string
}

export const CommitChartWithInvestors = ({ issue }: { issue: EnrichedIssueEntity }) => {
    const theme = useTheme()
    const { i18n } = useTranslation()
    const locale = i18n.language

    const chartColors = [
        { color: theme.palette.info.main },
        { color: theme.palette.info.light },
        { color: theme.palette.info[200] },
        { color: theme.palette.info[300] },
        { color: theme.palette.info[400] },
        { color: theme.palette.info[500] },
        { color: theme.palette.info[700] },
        { color: theme.palette.info[900] },
    ]

    const { investors } = issue

    const investorsExcludingSellers = investors.filter((investor) => !investor.isSeller)
    const maxFeeLevel = Math.max(...investorsExcludingSellers.map((investor) => investor.feeLevel))
    const secondaryOffering = issue.secondaryOffering !== null ? issue.secondaryOffering : 0
    const { totalOfferingSize } = calculateIssueTotalOfferingSize(issue)
    const summarizedFeeLevels = getSummarizedFeeLevels(investorsExcludingSellers, totalOfferingSize)

    const sumSubscriptionCommitmentInPercentage = calculateSumSubscriptionCommitmentInPercentage({
        investors: investorsExcludingSellers,
        totalOfferingSize,
    })

    const sumGarantCommitmentInPercentage = investorsExcludingSellers.reduce(
        (acc, investor) => (investor.feeLevel > 0 ? acc + (investor.amountInPercentage || 0) : acc),
        0,
    )
    const sumGarantCommitmentInCash = investorsExcludingSellers.reduce(
        (acc, investor) => (investor.feeLevel > 0 ? acc + (investor.amountInCash || 0) : acc),
        0,
    )

    const totalSubscriptionCommitmentInPercentage = sumSubscriptionCommitmentInPercentage

    const totalGarantCommitmentInPercentage =
        sumGarantCommitmentInPercentage > 0
            ? sumGarantCommitmentInPercentage
            : (sumGarantCommitmentInCash * 100) / ((issue.totalOfferedUnits || 0) + secondaryOffering)

    const notGuaranteed =
        100 - totalGarantCommitmentInPercentage - totalSubscriptionCommitmentInPercentage - issue.approxTotalCommitment

    const commitment = summarizedFeeLevels.find((feeLevel) => feeLevel.feeLevel === 0)

    let notAssuredInPercentage = notGuaranteed

    const series = summarizedFeeLevels.reduce((acc: SerieType[], item) => {
        const { feeLevel, amountInPercentage } = item
        const feeLevelObject = issue.fees.find((fee) => fee.feeLevel === feeLevel)
        const color = chartColors[feeLevel] ? chartColors[feeLevel].color : randomBlue()

        if (feeLevelObject?.middleDown) {
            const notAssured = feeLevelObject.middleDown - commitment?.amountInPercentage || 0
            if (notAssured > 0) {
                notAssuredInPercentage -= notAssured
                acc.push({
                    stack: 'A',
                    label: 'Ej säkerställt',
                    data: [roundFloat(notAssured)],
                    valueFormatter: valueFormatter(locale),
                    color: theme.palette.grey[200],
                })
            }
            acc.push({
                stack: 'A',
                label: getLevelName(feeLevel, maxFeeLevel),
                data: [roundFloat(amountInPercentage)],
                valueFormatter: valueFormatter(locale),
                color,
            })
            return acc
        }

        if (feeLevelObject?.topDown && notAssuredInPercentage > 0) {
            acc.push({
                stack: 'A',
                label: 'Ej säkerställt',
                data: [roundFloat(notAssuredInPercentage)],
                valueFormatter: valueFormatter(locale),
                color: theme.palette.grey[200],
            })
            notAssuredInPercentage = 0
        }

        acc.push({
            stack: 'A',
            label: getLevelName(feeLevel, maxFeeLevel),
            data: [roundFloat(amountInPercentage)],
            valueFormatter: valueFormatter(locale),
            color,
        })

        return acc
    }, [])

    if (notAssuredInPercentage > 0) {
        series.push({
            stack: 'A',
            label: 'Ej säkerställt',
            data: [roundFloat(notAssuredInPercentage)],
            valueFormatter: valueFormatter(locale),
            color: theme.palette.grey[200],
        })
        notAssuredInPercentage = 0
    }

    return <CommitChart series={series} locale={locale} />
}
